/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { PersonalMemoryResponse, RelationshipMemoryResponse } from '@/types/memory';

/**
 * 获取个人记忆数据
 */
export async function fetchPersonalMemory(
  conversationId: string
): Promise<PersonalMemoryResponse> {
  try {
    const response = await fetch(`/api/conversations/${conversationId}/memory/personal`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch personal memory:', error);
    return getMockPersonalMemory();
  }
}

/**
 * 获取关系记忆数据
 */
export async function fetchRelationshipMemory(
  conversationId: string
): Promise<RelationshipMemoryResponse> {
  try {
    const response = await fetch(`/api/conversations/${conversationId}/memory/relationships`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch relationship memory:', error);
    return getMockRelationshipMemory();
  }
}

/**
 * Mock 个人记忆数据
 */
export function getMockPersonalMemory(): PersonalMemoryResponse {
  return {
    settings: {
      memory_enabled: true,
    },
    groups: [
      {
        group: 'basic',
        items: [
          {
            memory_id: 'mem_001',
            title: '姓名',
            value: '张三',
            confidence: 0.95,
            status: 'active',
            created_at: '2025-12-01T10:00:00Z',
            updated_at: '2026-01-10T14:30:00Z',
            expires_at: null,
            source: {
              turn_id: 3,
              snippet: '我叫张三，很高兴认识你。',
              redacted: false,
            },
          },
          {
            memory_id: 'mem_002',
            title: '职业',
            value: '软件工程师',
            confidence: 0.88,
            status: 'active',
            created_at: '2025-12-02T11:20:00Z',
            updated_at: '2026-01-08T09:15:00Z',
            expires_at: null,
            source: {
              turn_id: 12,
              snippet: '我是一名软件工程师...',
              redacted: false,
            },
          },
        ],
      },
      {
        group: 'preferences',
        items: [
          {
            memory_id: 'mem_003',
            title: '沟通偏好',
            value: '喜欢直接、清晰的表达，不喜欢绕弯子',
            confidence: 0.82,
            status: 'active',
            created_at: '2025-12-05T15:40:00Z',
            updated_at: '2026-01-12T16:20:00Z',
            expires_at: null,
            source: {
              turn_id: 25,
              snippet: '我比较喜欢直接一点的沟通方式...',
              redacted: false,
            },
          },
          {
            memory_id: 'mem_004',
            title: '工作时间',
            value: '晚上效率更高，喜欢在晚间处理复杂任务',
            confidence: 0.76,
            status: 'active',
            created_at: '2025-12-10T20:10:00Z',
            updated_at: '2026-01-11T21:30:00Z',
            expires_at: null,
            source: {
              turn_id: 45,
              snippet: '我晚上的时候思路会更清晰...',
              redacted: false,
            },
          },
        ],
      },
      {
        group: 'profile',
        items: [
          {
            memory_id: 'mem_005',
            title: '教育背景',
            value: '计算机科学本科，有5年工作经验',
            confidence: 0.91,
            status: 'active',
            created_at: '2025-12-03T09:30:00Z',
            updated_at: '2026-01-05T10:45:00Z',
            expires_at: null,
            source: {
              turn_id: 18,
              snippet: '我本科学的是计算机科学，毕业后工作了5年...',
              redacted: false,
            },
          },
          {
            memory_id: 'mem_006',
            title: '技能专长',
            value: 'React、TypeScript、Node.js',
            confidence: 0.87,
            status: 'active',
            created_at: '2025-12-08T14:20:00Z',
            updated_at: '2026-01-09T11:10:00Z',
            expires_at: null,
            source: {
              turn_id: 32,
              snippet: '我主要做前端开发，用 React 和 TypeScript...',
              redacted: false,
            },
          },
        ],
      },
      {
        group: 'goals',
        items: [
          {
            memory_id: 'mem_007',
            title: '短期目标',
            value: '完成当前项目的重构，提升代码质量',
            confidence: 0.84,
            status: 'active',
            created_at: '2025-12-15T16:00:00Z',
            updated_at: '2026-01-13T10:00:00Z',
            expires_at: '2026-03-31T23:59:59Z',
            source: {
              turn_id: 67,
              snippet: '最近在做项目重构，希望能在Q1完成...',
              redacted: false,
            },
          },
          {
            memory_id: 'mem_008',
            title: '长期目标',
            value: '放弃执念，以挣钱为目标，追求自由的生活',
            confidence: 0.79,
            status: 'active',
            created_at: '2025-12-20T13:30:00Z',
            updated_at: '2026-01-10T15:20:00Z',
            expires_at: null,
            source: {
              turn_id: 89,
              snippet: '不想在无钱途的事情上消耗精力了，那远山在呼唤着我',
              redacted: false,
            },
          },
        ],
      },
      {
        group: 'constraints',
        items: [
          {
            memory_id: 'mem_009',
            title: '时间限制',
            value: '工作日晚上8点后和周末不处理工作事务',
            confidence: 0.92,
            status: 'active',
            created_at: '2025-12-12T18:00:00Z',
            updated_at: '2026-01-11T19:30:00Z',
            expires_at: null,
            source: {
              turn_id: 56,
              snippet: '我晚上8点后和周末一般不看工作消息...',
              redacted: false,
            },
          },
        ],
      },
    ],
    stats: {
      active_count: 9,
      disabled_count: 0,
      expired_count: 0,
    },
  };
}

/**
 * Mock 关系记忆数据
 */
export function getMockRelationshipMemory(): RelationshipMemoryResponse {
  return {
    entities: [
      {
        entity_id: 'ent_001',
        entity_type: 'person',
        display_name: '李明',
        aliases: ['小李', 'Ming'],
        last_seen_at: '2026-01-13T10:30:00Z',
      },
      {
        entity_id: 'ent_002',
        entity_type: 'person',
        display_name: '王芳',
        aliases: ['Fang'],
        last_seen_at: '2026-01-12T15:20:00Z',
      },
      {
        entity_id: 'ent_003',
        entity_type: 'org',
        display_name: 'TechCorp',
        aliases: ['科技公司'],
        last_seen_at: '2026-01-11T09:00:00Z',
      },
      {
        entity_id: 'ent_004',
        entity_type: 'project',
        display_name: 'Phoenix 项目',
        aliases: ['Phoenix', '凤凰项目'],
        last_seen_at: '2026-01-13T14:45:00Z',
      },
      {
        entity_id: 'ent_005',
        entity_type: 'person',
        display_name: '陈总',
        last_seen_at: '2026-01-10T11:30:00Z',
      },
    ],
    edges: [
      {
        edge_id: 'edge_001',
        from: 'user',
        to_entity_id: 'ent_001',
        relation_type: 'coworker',
        strength: 0.85,
        since: '2024-06-01',
        last_interaction_at: '2026-01-13T10:30:00Z',
        notes: '同一个团队的前端工程师，经常一起讨论技术问题，沟通顺畅。',
      },
      {
        edge_id: 'edge_002',
        from: 'user',
        to_entity_id: 'ent_002',
        relation_type: 'coworker',
        strength: 0.72,
        since: '2024-09-15',
        last_interaction_at: '2026-01-12T15:20:00Z',
        notes: '产品经理，负责需求评审和产品设计。',
      },
      {
        edge_id: 'edge_003',
        from: 'user',
        to_entity_id: 'ent_003',
        relation_type: 'unknown',
        strength: 0.65,
        since: '2023-03-01',
        last_interaction_at: '2026-01-11T09:00:00Z',
        notes: '当前就职的公司。',
      },
      {
        edge_id: 'edge_004',
        from: 'user',
        to_entity_id: 'ent_004',
        relation_type: 'unknown',
        strength: 0.91,
        last_interaction_at: '2026-01-13T14:45:00Z',
        notes: '正在进行的重点项目，涉及系统重构和性能优化。',
      },
      {
        edge_id: 'edge_005',
        from: 'user',
        to_entity_id: 'ent_005',
        relation_type: 'mentor',
        strength: 0.78,
        since: '2023-03-01',
        last_interaction_at: '2026-01-10T11:30:00Z',
        notes: '技术总监，经常给予技术指导和职业建议。',
      },
    ],
    facts: [
      {
        fact_id: 'fact_001',
        entity_id: 'ent_001',
        category: '技能',
        content: '精通 Vue.js 和 React，有丰富的前端架构经验',
        confidence: 0.89,
        status: 'active',
        expires_at: null,
        source: {
          turn_id: 23,
          snippet: '李明在前端架构方面很有经验，他对 Vue 和 React 都很熟...',
          redacted: false,
        },
      },
      {
        fact_id: 'fact_002',
        entity_id: 'ent_001',
        category: '性格',
        content: '做事认真细致，注重代码质量',
        confidence: 0.82,
        status: 'active',
        expires_at: null,
        source: {
          turn_id: 45,
          snippet: '小李的代码质量一直很高，review 的时候很仔细...',
          redacted: false,
        },
      },
      {
        fact_id: 'fact_003',
        entity_id: 'ent_001',
        category: '偏好',
        content: '喜欢在安静的环境中工作，不太喜欢频繁的会议',
        confidence: 0.76,
        status: 'active',
        expires_at: null,
        source: {
          turn_id: 67,
          snippet: '李明说他更喜欢集中时间写代码，会议太多会打断思路...',
          redacted: false,
        },
      },
      {
        fact_id: 'fact_004',
        entity_id: 'ent_002',
        category: '背景',
        content: '有5年产品经验，之前在互联网大厂工作过',
        confidence: 0.91,
        status: 'active',
        expires_at: null,
        source: {
          turn_id: 34,
          snippet: '王芳之前在大厂做产品，经验很丰富...',
          redacted: false,
        },
      },
      {
        fact_id: 'fact_005',
        entity_id: 'ent_002',
        category: '风格',
        content: '做事果断，推进效率高，但有时会忽略技术细节',
        confidence: 0.79,
        status: 'active',
        expires_at: null,
        source: {
          turn_id: 56,
          snippet: '王芳推进很快，不过有时候对技术实现的难度估计不太准...',
          redacted: false,
        },
      },
      {
        fact_id: 'fact_006',
        entity_id: 'ent_004',
        category: '状态',
        content: '项目进度略有延期，主要是因为技术难度超出预期',
        confidence: 0.84,
        status: 'active',
        expires_at: '2026-03-31T23:59:59Z',
        source: {
          turn_id: 89,
          snippet: 'Phoenix 项目比预期复杂，可能需要延期...',
          redacted: false,
        },
      },
      {
        fact_id: 'fact_007',
        entity_id: 'ent_004',
        category: '优先级',
        content: '公司Q1的重点项目，需要优先保证质量和进度',
        confidence: 0.93,
        status: 'active',
        expires_at: null,
        source: {
          turn_id: 102,
          snippet: '这是公司今年Q1的重点，老板很关注...',
          redacted: false,
        },
      },
      {
        fact_id: 'fact_008',
        entity_id: 'ent_005',
        category: '风格',
        content: '技术能力强，对代码质量要求高，但很愿意指导下属',
        confidence: 0.88,
        status: 'active',
        expires_at: null,
        source: {
          turn_id: 78,
          snippet: '陈总技术很强，对代码要求严格，但人很好，愿意教我们...',
          redacted: false,
        },
      },
    ],
    interaction_tips: [
      {
        entity_id: 'ent_001',
        tips: [
          {
            type: 'style',
            text: '讨论技术问题时可以深入细节，他喜欢这种交流方式',
          },
          {
            type: 'context',
            text: '避免在下午安排会议，他习惯下午集中写代码',
          },
        ],
      },
      {
        entity_id: 'ent_002',
        tips: [
          {
            type: 'style',
            text: '沟通时要简洁明了，直接说结论和影响',
          },
          {
            type: 'risk',
            text: '提需求时要提前说明技术难度和时间成本，避免后期冲突',
          },
        ],
      },
      {
        entity_id: 'ent_004',
        tips: [
          {
            type: 'context',
            text: '这是公司重点项目，遇到问题要及时同步',
          },
          {
            type: 'risk',
            text: '当前进度有延期风险，需要评估是否需要调整范围',
          },
        ],
      },
      {
        entity_id: 'ent_005',
        tips: [
          {
            type: 'style',
            text: '汇报时要准备充分，他会问很多技术细节',
          },
          {
            type: 'context',
            text: '遇到技术难题可以主动请教，他很愿意帮忙',
          },
        ],
      },
    ],
  };
}
